import Vue from 'vue';
import VueRouter from 'vue-router';
import middleware from './middleware';

// catch NavigationDuplicated errors in router.push, router.replace
['push', 'replace'].forEach(fnName => {
    const originalFn = VueRouter.prototype[fnName];
    VueRouter.prototype[fnName] = function(location) {
        originalFn.call(this, location).catch(err => {
            if (
                typeof err !== 'undefined' &&
                err.name !== 'NavigationDuplicated' &&
                !err.message.includes('Avoided redundant navigation')
            ) {
                throw err;
            }
        });
    };
});

Vue.use(VueRouter);

const routes = [
    {
        path: '/',
        name: 'Home',
        meta: { title: 'Home' },
        component: () => import('../views/Home.vue'),
    },
    {
        path: '/library(/folder.*)?',
        name: 'FormLibrary',
        meta: { title: "Library", minRole: 'user' },
        component: () => import(/* webpackChunkName: "library.prefetch" */ '../views/FormLibrary.vue'),
    },
    {
        path: '/builder/:formId?',
        name: 'FormBuilder',
        meta: { title: 'Form Builder', minRole: 'editor' },
        component: () => import('../views/Builder.vue'),
    },
    {
        path: '/f/:altRegionCode/:token?',
        alias: [
            '/public/:altRegionCode/:token?',
            '/d/:altRegionCode/:token?' // device link
        ],
        name: 'FillForm',
        meta: { customTitle: true },
        component: () => import('../views/Filler.vue'),
    },
    {
        path: '/terms',
        name: 'SignatureTerms',
        meta: { title: 'eSignature Terms' },
        component: () => import('../views/SignatureTerms.vue')
    },
    {
        path: '/view/:revisionRef',
        name: 'ViewForm',
        meta: { customTitle: true },
        component: () => import('../views/Viewer.vue'),
    },
    {
        path: '/office/:mode/:pkgId',
        name: 'Office',
        meta: { title: 'Office View', minRole: 'user' },
        component: () => import(/* webpackChunkName: "office.prefetch" */ '../views/Office.vue'),
    },
    {
        path: '/send',
        name: 'SendForm',
        meta: { title: 'Office View', minRole: 'user' },
        component: () => import('../views/SendForm.vue'),
    },
    {
        path: '/list',
        redirect: '/boxes'
    },
    {
        path: '/list/:filter',
        redirect: '/boxes/0/:filter'
    },
    {
        path: '/boxes',
        redirect: 'boxes/0/inbox'
    },
    {
        path: '/boxes/:boxIndex/:filter',
        name: 'Boxes',
        meta: { customTitle: true, minRole: 'user' },
        component: () => import(/* webpackChunkName: "inbox.prefetch" */ '../views/Boxes.vue')
    },
    {
        path: '/dataElementsList/:defId',
        name: 'DataElementsList',
        meta: { customTitle: true, minRole: 'user' },
        component: () => import('../views/DataElementsList.vue')
    },
    {
        path: '/settings',
        component: () => import('../views/Settings.vue'),
        children: [
            {
                path: '/',
                redirect: 'profile'
            },
            {
                path: 'profile',
                name: 'Profile',
                meta: { title: 'Profile Settings', minRole: 'user' },
                component: () => import('../components/settings/SettingsProfile.vue')
            },
            {
                path: 'password',
                name: 'ChangePassword',
                meta: { title: 'Change Password', minRole: 'user' },
                component: () => import('../components/settings/SettingsPassword.vue')
            },
            {
                path: 'notifications',
                name: 'Notifications',
                meta: { title: 'Notification Settings', minRole: 'user' },
                component: () => import('../components/settings/SettingsNotifications.vue')
            },
            {
                path: 'teammates',
                name: 'Teammates',
                meta: { title: 'Teammates Settings', minRole: 'admin' },
                component: () => import('../components/settings/SettingsTeammates.vue'),
                children: [
                    { path: "user/:userId?" },
                ]
            },
            {
                path: 'teammates/t/:tab',
                name: 'TeammatesTab',
                meta: { title: 'Teammates Settings', minRole: 'admin' },
                component: () => import('../components/settings/SettingsTeammates.vue')
            },
            {
                path: "teammates/invite",
                meta: { title: 'Invite Teammates', minRole: 'admin' },
                component: () => import('../components/settings/SettingsTeammateInvite.vue'),
                children: [
                    { path: ':inviteToken' }
                ]
            },
            {
                path: 'billing',
                name: 'Billing',
                meta: { title: 'Billing Settings', minRole: 'owner' },
                component: () => import('../components/settings/SettingsBilling.vue')
            },
            {
                path: 'appearance',
                name: 'Appearance',
                meta: { title: 'Appearance Settings', minRole: 'admin' },
                component: () => import('../components/settings/SettingsAppearance.vue')
            },
            {
                path: 'boxes/:boxType?/:boxId?',
                name: 'SharedBoxes',
                meta: { title: 'Shared Boxes', minRole: 'admin' },
                component: () => import('../views/BoxesManager.vue')
            },
            {
                path: 'labels',
                name: 'CustomLabels',
                meta: { title: 'Custom Labels', minRole: 'admin' },
                component: () => import('../components/settings/CustomLabels.vue')
            },
            {
                path: 'organization',
                name: 'OrganizationSettings',
                meta: { title: 'Organization Settings', minRole: 'admin' },
                component: () => import('../components/settings/SettingsOrganization.vue')
            },
            {
                path: 'payments',
                name: 'Payments',
                meta: { title: 'Payments', minRole: 'admin' },
                component: () => import('../components/settings/SettingsPayments.vue')
            },
            {
                path: 'api',
                name: 'APISettings',
                meta: { title: 'API Settings', minRole: 'admin' },
                component: () => import('../components/settings/SettingsAPI.vue')
            },
            {
                path: 'webhooks',
                name: 'Webhooks',
                meta: { title: 'Webhooks', minRole: 'admin' },
                component: () => import('../components/settings/SettingsWebhooks.vue')
            }
        ]
    },
    {
        path: '/signup/:inviteToken?',
        alias: '/login',
        name: 'Login',
        component: () => import('../views/Login.vue')
    },
    {
        path: '/reset-password',
        name: 'ResetPassword',
        component: () => import('../views/ResetPassword.vue')
    },
    {
        path: '/pdf',
        name: 'WaitForPDF',
        meta: { title: 'View PDF' },
        component: () => import('../views/WaitForPDF.vue'),
    },
    {
        path: '/payment/:processor/:altRegionCode?',
        name: 'PaymentRedirect',
        component: () => import('../views/PaymentRedirect.vue')
    },
    {
        path: '/maintenance',
        name: 'MaintenanceMode',
        component: () => import('../views/MaintenanceMode.vue')
    },
    {
        path: '/embed',
        name: 'Embed',
        component: () => import('../views/Embed.vue'),
    },
    {
        path: '/admin',
        component: () => import('../views/Admin.vue'),
        meta: { title: 'Administration' },
        children: [
            {
                path: '/',
                redirect: 'organizations'
            },
            {
                path: 'organizations',
                name: 'AdminOrganizations',
                component: () => import('../components/admin/Organizations.vue'),
            },
            {
                path: 'organizations/:orgId',
                name: 'AdminOrganization',
                component: () => import('../components/admin/Organization.vue'),
            },
            {
                path: 'partners',
                name: 'AdminPartners',
                component: () => import('../components/admin/Partners.vue'),
            },
            {
                path: 'partners/:partnerId',
                name: 'AdminPartner',
                component: () => import('../components/admin/Partner.vue'),
            },
        ]
    },
    {
        path: '*',
        name: 'NotFound',
        component: () => import('../views/NotFound.vue')
    }
];

if (process.env !== 'production') {
    routes.push({
        path: '/styleguide',
        name: 'StyleGuide',
        meta: { title: 'Style Guide' },
        component: () => import('../views/StyleGuide.vue')
    });

    routes.push({
        path: '/apitest',
        name: 'ApiTest',
        meta: { title: 'API Test' },
        component: () => import('../views/ApiTest.vue')
    });
}

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
});

router.beforeEach(middleware.waitForEnv);
router.beforeEach(middleware.checkAppStatus);
router.beforeEach(middleware.clearNotifications);
router.beforeEach(middleware.updateNavBar);
router.beforeEach(middleware.loadUser);
router.beforeEach(middleware.loadPartnerBranding);
router.beforeEach(middleware.updateMetadata);
router.beforeEach(middleware.checkRole);

router.afterEach(middleware.updateCurrentRouteInStore);

export default router;